import {useTranslations} from 'use-intl'
import {useMemo} from 'react'
import clsx from 'clsx'
import {ProductWithIds} from '~/client/spreadshop/missing-types'
import ImageComposition from '~/components/ImageComposition'
import useShopConfig from '~/hooks/useShopConfig'
import {Image, ImageFormat} from '~/gql/graphql'

type MaterialImageProps = {
    className?: string
    product: ProductWithIds
    appearanceId?: string
}

const MATERIAL_VIEW_ID = '12'

const MaterialImage = ({
    product,
    appearanceId: preferredAppearanceId,
    className,
}: MaterialImageProps) => {
    const t = useTranslations('productDetails.productTypeDialog')
    const {imageServerEndpoint} = useShopConfig()

    const image = useMemo<Image | undefined>(() => {
        const hasMaterialImage = !!product.views?.includes(MATERIAL_VIEW_ID)
        const appearanceId = preferredAppearanceId ?? product.defaultAppearanceId
        const productTypeId = product.type?.id

        if (!hasMaterialImage || !appearanceId || !productTypeId) {
            return undefined
        }

        const imageUrl = new URL(
            `${imageServerEndpoint}/productTypes/${productTypeId}/views/${MATERIAL_VIEW_ID}/appearances/${appearanceId}.webp`
        )

        return {
            src: imageUrl.toString(),
            srcset: [100, 500, 1000]
                .map((size) => {
                    imageUrl.searchParams.set('width', String(size))
                    imageUrl.searchParams.set('height', String(size))
                    return `${imageUrl.toString()} ${size}w`
                })
                .join(','),
            altText: t('materialImageAlt'),
            format: ImageFormat.Png,
        }
    }, [
        preferredAppearanceId,
        imageServerEndpoint,
        product.views,
        product.type?.id,
        product.defaultAppearanceId,
        t,
    ])

    if (!image) {
        return null
    }

    return (
        <ImageComposition
            className={clsx(className, 'rounded-lg overflow-hidden')}
            image={image}
            sizes="(min-width: 640px) 1000px, 500px"
        />
    )
}

export default MaterialImage
